import { Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { SupplierSidebar } from '@/components/supplier/SupplierSidebar';
import { SupplierTopNav } from '@/components/supplier/SupplierTopNav';
import { MobileBottomNav } from '@/components/navigation/MobileBottomNav';
import { SupplierMobileHeader } from '@/components/supplier/SupplierMobileHeader';
import { useIsMobile } from '@/hooks/use-mobile';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { SkipLink } from '@/components/accessibility/SkipLink';
import { LayoutProvider } from '@/contexts/LayoutContext';
import { cn } from '@/lib/utils';
import { PageTransition } from '@/components/layout/PageTransition';

// PERFORMANCE: Lazy load non-critical components
const AIAssistant = lazy(() => import('@/components/ai/AIAssistant').then(m => ({ default: m.AIAssistant })));

function SupplierLayoutContent() {
  const isMobile = useIsMobile();
  const { isCollapsed } = useSidebar();

  if (isMobile) {
    return (
      <div className="flex min-h-dvh w-full flex-col bg-background">
        <SkipLink />
        <div className="px-4 pt-4">
          <ImpersonationBanner />
        </div>
        <header>
          <SupplierMobileHeader />
        </header>
        <main
          id="main-content"
          data-scroll-container
          className="ios-smooth-scroll flex-1 overflow-y-auto bg-background pb-[calc(5rem+env(safe-area-inset-bottom,0px))]"
        >
          <PageTransition variant="fade">
            <Outlet />
          </PageTransition>
        </main>
        <nav aria-label="Mobile navigation">
          <MobileBottomNav />
        </nav>
        
        {/* AI Assistant - lazy loaded */}
        <Suspense fallback={null}>
          <AIAssistant />
        </Suspense>
      </div>
    );
  }

    return (
      <div className="flex h-screen w-full overflow-hidden bg-background">
        <SkipLink />
        {/* Desktop Sidebar - Navigation */}
        <aside
          className={cn(
            "hidden md:flex bg-white transition-all duration-250 ease-in-out border-r border-border h-screen",
            isCollapsed ? "w-[72px]" : "w-[280px]"
          )}
          aria-label="Main navigation"
        >
          <div className="sticky top-0 h-screen w-full">
            <SupplierSidebar />
          </div>
        </aside>
      
        {/* Main Content */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Impersonation Banner */}
          <div className="px-4 pt-4 md:px-6">
            <ImpersonationBanner />
          </div>
          
          {/* Desktop Top Nav (only on desktop) */}
          <header className="hidden md:block">
            <SupplierTopNav />
          </header>
          
          {/* Mobile Header (only on mobile) */}
          <header className="md:hidden">
            <SupplierMobileHeader />
          </header>
          
          {/* Page Content */}
          <main
            id="main-content"
            data-scroll-container
            className="ios-smooth-scroll min-h-0 flex-1 overflow-y-auto bg-background px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] pt-4 sm:px-6 md:pb-8 md:pt-6 lg:px-8"
          >
            <PageTransition variant="fade">
              <Outlet />
            </PageTransition>
          </main>
          
          {/* Mobile Bottom Navigation */}
          <nav className="md:hidden" aria-label="Mobile navigation">
            <MobileBottomNav />
          </nav>
        </div>

      {/* AI Assistant - lazy loaded */}
      <Suspense fallback={null}>
        <AIAssistant />
      </Suspense>
    </div>
  );
}

export function SupplierLayout() {
  return (
    <SidebarProvider>
      <LayoutProvider>
        <SupplierLayoutContent />
      </LayoutProvider>
    </SidebarProvider>
  );
}
